import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import type { OrganizationKind } from "@/shared/organizations/organization-link";
import { ORGANIZATION_KINDS, ORGANIZATIONS_SEARCH_MAX_LENGTH } from "./constants";

interface OrganizationsFiltersProps {
  /** What the operator typed (applied after a pause by the page). */
  readonly search: string;
  readonly kind: OrganizationKind | null;
  readonly onSearchChange: (search: string) => void;
  readonly onKindChange: (kind: OrganizationKind | null) => void;
  readonly onReset: () => void;
}

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring";
const LABEL_CLASS = "font-sans text-sm font-medium text-foreground";

/**
 * Search by organization or owner name (never an email: the API does not match
 * one) and the kind filter. Both apply as the operator changes them; the page
 * debounces the search so a typed name is one request, not one per key.
 */
export function OrganizationsFilters({
  search,
  kind,
  onSearchChange,
  onKindChange,
  onReset,
}: OrganizationsFiltersProps) {
  const { t } = useTranslation();
  const dirty = search.length > 0 || kind !== null;

  return (
    <form
      role="search"
      aria-label={t("organizations.list.filters.label")}
      data-testid="organizations-filters"
      onSubmit={(event) => event.preventDefault()}
      className="flex flex-wrap items-end gap-3"
    >
      <div className="min-w-[16rem] space-y-1.5">
        <label htmlFor="organizations-search" className={LABEL_CLASS}>
          {t("organizations.list.filters.search")}
        </label>
        <Input
          id="organizations-search"
          data-testid="organizations-filter-search"
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          maxLength={ORGANIZATIONS_SEARCH_MAX_LENGTH}
          placeholder={t("organizations.list.filters.searchPlaceholder")}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="organizations-kind" className={LABEL_CLASS}>
          {t("organizations.list.filters.kind")}
        </label>
        <select
          id="organizations-kind"
          data-testid="organizations-filter-kind"
          value={kind ?? ""}
          onChange={(e) =>
            onKindChange(e.target.value === "" ? null : (e.target.value as OrganizationKind))
          }
          className={SELECT_CLASS}
        >
          <option value="">{t("organizations.list.filters.kindAll")}</option>
          {ORGANIZATION_KINDS.map((value) => (
            <option key={value} value={value}>
              {t(`organizations.kind.${value}`)}
            </option>
          ))}
        </select>
      </div>

      {dirty && (
        <Button
          type="button"
          data-testid="organizations-filters-reset"
          variant="outline"
          onClick={onReset}
        >
          {t("organizations.list.filters.reset")}
        </Button>
      )}
    </form>
  );
}
